import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TEngagementPlan, THCP, THCPProjectsData, buildHCPMock } from '@shared/interfaces/THCP';
import { newGuid } from '@shared/utils/newGuild';
import { Subscription } from 'rxjs';
import { TComponentType, THCPParams } from 'src/app/interfaces/component-params.interface';
import { CategoriesService } from 'src/app/services/categories.service';
import { HCPService } from 'src/app/services/hcp.service';
import { LoadingService } from 'src/app/services/loading.service';
import { AuthService, TCurrentUser } from '../../../../../services/auth.service';
import { EngagementUtils } from '../hcp-profile-tab.utils';

@Component({
  selector: 'app-engagement-plan',
  templateUrl: './engagement-plan.component.html',
  styleUrls: ['./engagement-plan.component.scss'],
})
export class EngagementPlanComponent implements OnInit, OnDestroy {
  @Input()
  public hcpData: THCP = {
    ...buildHCPMock({}),
  };
  @Input()
  public params: THCPParams = {
    hcpId: '',
    categoryId: '',
    projectId: '',
    __type: TComponentType.hcp,
  };
  public engagementScore: number = 0;
  private _subs: Subscription[] = [];
  public headers = EngagementUtils.getEngagementPlanHeaders();
  private projectsDescriptions: Map<string, string> = new Map();

  constructor(
    private _HCPsService: HCPService,
    private _autService: AuthService,
    private _categoriesService: CategoriesService,
    private _loadingService: LoadingService
  ) {}

  public ngOnInit(): void {
    this._subs.push(
      this._HCPsService.getExtendedHCPsObservable().subscribe((hcps) => {
        if (hcps.length === 1) {
          const projectData: THCPProjectsData =
            this._HCPsService.getProjectData(hcps[0], this.params.projectId);
          this.engagementScore = projectData.engagementScore;
        }
      })
    );
    this._subs.push(
      this._categoriesService
        .getCategoriesObservable()
        .subscribe((categories) => {
          categories.forEach((c) => {
            c.projects.forEach((p) => {
              this.projectsDescriptions.set(p.id, p.description);
            });
          });
        })
    );
  }

  public getEngagements(): TEngagementPlan[] {
    const allEngagements: TEngagementPlan[] = this.hcpData.projectsData
      .map((it) => it.engagement_plan ?? [])
      .flat();
    return allEngagements;
  }

  public getProjectFromEngagement(engagement: TEngagementPlan): {
    id: string;
    description: string;
  } {
    let description: string = `N/A`;
    let projectId: string = '';
    this.hcpData.projectsData.forEach((it) => {
      (it.engagement_plan ?? []).forEach((e) => {
        if (e.id === engagement.id) {
          description = this.projectsDescriptions.get(it.projectId) ?? `N/A`;
          projectId = it.projectId;
        }
      });
    });
    return {
      description,
      id: projectId,
    };
  }

  public addNewEngagement(): void {
    const user: TCurrentUser = this._autService.getCurrentUser();
    const newEngagement: TEngagementPlan = {
      engagement_plan_accountable: `${user?.firstName ?? `N/A`} ${
        user?.lastName ?? `N/A`
      }`,
      engagement_plan_completed: false,
      engagement_plan_objectives: `...`,
      engagement_plan_time: new Date(),
      id: newGuid(),
      tags: [],
    };
    this._getEngagementsForProject().unshift(newEngagement);
  }

  public ngOnDestroy(): void {
    this._subs.forEach((s) => s.unsubscribe());
  }

  public getSortedEngagements(): TEngagementPlan[] {
    return this.getEngagements().sort((a, b) => {
      return (
        new Date(b.engagement_plan_time!).getTime() -
        new Date(a.engagement_plan_time!).getTime()
      );
    });
  }

  public deleteCallback(item: TEngagementPlan): void {
    let engagements: TEngagementPlan[] = [];
    this.hcpData.projectsData.forEach((it) => {
      if (
        it.engagement_plan?.find((engagement) => engagement.id === item.id) ??
        -1 > -1
      ) {
        engagements = it.engagement_plan;
      }
    });
    const itemTORemoveIndex: number = engagements.findIndex(
      (it) => it.id === item.id
    );
    if (itemTORemoveIndex === -1) {
      this._loadingService.triggerInfoMessage(`Item not found`);
    } else {
      engagements.splice(itemTORemoveIndex, 1);
    }
  }

  private _getEngagementsForProject(): TEngagementPlan[] {
    return (
      this.hcpData.projectsData.find(
        (it) => it.projectId === this.params.projectId
      )?.engagement_plan ?? []
    );
  }
}
